/**
 * <wholesale-form>
 * Progressive enhancement for the wholesale lead form (Shopify contact form).
 * - Inline validation with the browser's validity API (no library).
 * - Scrolls to and focuses the first invalid field; summary in a live region.
 * - Joins the "Interested in" checkboxes into one contact[Interested in] value.
 * - Honeypot: silently refuses to post when the hidden field is filled.
 * - Disables double-submit and shows the spinner.
 *
 * Messages come from data-error-* attributes on the element (translated in Liquid).
 */
if (!customElements.get('wholesale-form')) {
  customElements.define(
    'wholesale-form',
    class WholesaleForm extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        if (!this.form) return;

        this.submitButton = this.querySelector('[type="submit"]');
        this.spinner = this.querySelector('.loading__spinner');
        this.live = this.querySelector('[data-live]');
        this.honeypot = this.querySelector('[data-honeypot]');
        this.joined = this.querySelector('[data-interests-joined]');
        this.fields = Array.from(this.querySelectorAll('input, select, textarea')).filter(
          (el) => el.type !== 'hidden' && el.type !== 'checkbox' && !el.hasAttribute('data-honeypot')
        );

        this.form.setAttribute('novalidate', 'novalidate');
        this.submitting = false;

        this.onSubmit = this.onSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit);

        this.fields.forEach((field) => {
          field.addEventListener('blur', () => {
            if (field.value !== '' || field.getAttribute('aria-invalid') === 'true') this.validateField(field);
          });
          field.addEventListener('input', () => {
            if (field.getAttribute('aria-invalid') === 'true') this.validateField(field);
          });
        });

        // Back/forward cache: the page may be restored with the button still disabled.
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) this.setBusy(false);
        });

        // A server-side error block (e.g. invalid email after post) should get focus.
        const serverErrors = this.querySelector('.wholesale-form__errors');
        if (serverErrors) serverErrors.focus({ preventScroll: false });
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      messageFor(field) {
        const v = field.validity;
        if (v.valueMissing) return this.dataset.errorRequired;
        if (field.type === 'email' && (v.typeMismatch || v.patternMismatch)) return this.dataset.errorEmail;
        if (field.type === 'tel' && v.patternMismatch) return this.dataset.errorPhone;
        if (field.type === 'url' && v.typeMismatch) return this.dataset.errorUrl;
        return field.validationMessage || this.dataset.errorRequired;
      }

      errorElement(field) {
        return document.getElementById(`${field.id}-error`);
      }

      validateField(field) {
        const valid = field.checkValidity();
        const errorEl = this.errorElement(field);

        if (valid) {
          field.removeAttribute('aria-invalid');
          if (errorEl) {
            errorEl.hidden = true;
            errorEl.textContent = '';
            this.removeDescribedBy(field, errorEl.id);
          }
          return true;
        }

        field.setAttribute('aria-invalid', 'true');
        if (errorEl) {
          errorEl.textContent = this.messageFor(field);
          errorEl.hidden = false;
          this.addDescribedBy(field, errorEl.id);
        }
        return false;
      }

      addDescribedBy(field, id) {
        const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (!ids.includes(id)) ids.push(id);
        field.setAttribute('aria-describedby', ids.join(' '));
      }

      removeDescribedBy(field, id) {
        const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter((x) => x && x !== id);
        if (ids.length) field.setAttribute('aria-describedby', ids.join(' '));
        else field.removeAttribute('aria-describedby');
      }

      joinInterests() {
        if (!this.joined) return;
        const checked = Array.from(this.querySelectorAll('[data-interest]:checked'));
        if (checked.length === 0) {
          this.joined.removeAttribute('name');
          return;
        }
        this.joined.value = checked.map((c) => c.dataset.interest).join(', ');
        this.joined.setAttribute('name', this.joined.dataset.name);
        // Individual boxes stay checked for the user but no longer post separately.
        this.querySelectorAll('[data-interest]').forEach((c) => {
          c.dataset.originalName = c.name;
          c.removeAttribute('name');
        });
      }

      restoreInterests() {
        this.querySelectorAll('[data-interest]').forEach((c) => {
          if (c.dataset.originalName) c.setAttribute('name', c.dataset.originalName);
        });
        this.joined?.removeAttribute('name');
      }

      setBusy(busy) {
        this.submitting = busy;
        if (!this.submitButton) return;
        this.submitButton.setAttribute('aria-disabled', String(busy));
        this.submitButton.classList.toggle('loading', busy);
        if (this.spinner) this.spinner.classList.toggle('hidden', !busy);
        if (busy && this.dataset.sending && this.live) this.live.textContent = this.dataset.sending;
        if (!busy) this.restoreInterests();
      }

      onSubmit(event) {
        if (this.submitting) {
          event.preventDefault();
          return;
        }

        // Honeypot filled → a bot. Pretend nothing happened.
        if (this.honeypot && this.honeypot.value.trim() !== '') {
          event.preventDefault();
          return;
        }

        const invalid = this.fields.filter((field) => !this.validateField(field));

        if (invalid.length > 0) {
          event.preventDefault();
          if (this.live) this.live.textContent = this.dataset.errorSummary || '';
          const first = invalid[0];
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          first.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
          first.focus({ preventScroll: true });
          return;
        }

        if (this.live) this.live.textContent = '';
        this.joinInterests();
        this.setBusy(true);
      }
    }
  );
}
